import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertMessage, Card, CardContent, CardHeader, CardTitle, Toast, ToastType } from "../../components/ui";
import { apiService } from "../../services/api";
import { cn } from "@/lib/utils";

type CatalogRow = {
  id: string;
  key: string;
  label: string;
  color?: string | null;
  tier: "spotlight" | "standard";
  sortOrder: number;
  isSelected: boolean;
};

function sortCatalogRows(rows: CatalogRow[]): CatalogRow[] {
  return [...rows].sort((a, b) => {
    const pa = a.tier === "spotlight" ? 0 : 1;
    const pb = b.tier === "spotlight" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label);
  });
}

export const LeadWorkingMediaPage: React.FC = () => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const sortedCatalog = useMemo(() => sortCatalogRows(catalog), [catalog]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLeadMediaCatalog();
      setCatalog(Array.isArray(data) ? data : []);
    } catch {
      setError(t("leadWorkingMedia.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleMedia = async (item: CatalogRow) => {
    if (item.key === "not_informed" && item.isSelected) {
      setToast({ message: t("leadWorkingMedia.errors.keepNotInformed"), type: "error" });
      return;
    }
    const selectedIds = catalog.filter((row) => row.isSelected).map((row) => row.id);
    const nextIds = item.isSelected ? selectedIds.filter((id) => id !== item.id) : [...selectedIds, item.id];
    const notInformedRow = catalog.find((row) => row.key === "not_informed");
    if (notInformedRow && !nextIds.includes(notInformedRow.id)) {
      setToast({ message: t("leadWorkingMedia.errors.keepNotInformed"), type: "error" });
      return;
    }
    try {
      setSavingId(item.id);
      const updated = await apiService.replaceWorkingLeadMedia(nextIds);
      setCatalog(Array.isArray(updated) ? updated : []);
      setToast({ message: t("leadWorkingMedia.success.updated"), type: "success" });
    } catch {
      setToast({ message: t("leadWorkingMedia.errors.update"), type: "error" });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {toast ? <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null}

      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("leadWorkingMedia.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("leadWorkingMedia.subtitle")}
        </p>
      </div>

      {error ? <AlertMessage variant="error" message={error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("leadWorkingMedia.catalogTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
          ) : sortedCatalog.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("leadWorkingMedia.empty")}</p>
          ) : (
            sortedCatalog.map((item) => {
              const rowBusy = savingId === item.id;
              const rowLocked = item.key === "not_informed" && item.isSelected;
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={rowBusy || rowLocked}
                  aria-pressed={item.isSelected}
                  aria-label={t("leadWorkingMedia.rowAria", {
                    label: item.label,
                    state: item.isSelected ? t("leadWorkingMedia.active") : t("leadWorkingMedia.inactive"),
                  })}
                  onClick={() => void toggleMedia(item)}
                  className={cn(
                    "group w-full rounded-lg border p-4 text-left transition-all duration-200",
                    "flex items-center justify-between gap-4",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    "disabled:pointer-events-none disabled:opacity-55",
                    "hover:-translate-y-px hover:shadow-md",
                    item.tier === "spotlight"
                      ? "border-primary/45 bg-primary/[0.07] hover:border-primary/55 hover:bg-primary/[0.1]"
                      : "border-border bg-card hover:border-primary/25 hover:bg-muted/40",
                    item.isSelected && "border-primary/55 shadow-sm ring-1 ring-primary/15",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{item.label}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {t("leadWorkingMedia.meta", {
                        key: item.key,
                        tier: t(`leadWorkingMedia.tier.${item.tier}`),
                      })}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span
                      className={cn(
                        "text-[11px] font-semibold uppercase tracking-wide",
                        item.isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {item.isSelected ? t("leadWorkingMedia.active") : t("leadWorkingMedia.inactive")}
                    </span>
                    <span
                      className={cn(
                        "relative flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200",
                        item.isSelected
                          ? "border-primary bg-primary ring-2 ring-primary/30 ring-offset-2 ring-offset-background"
                          : "border-muted-foreground/40 bg-background group-hover:border-primary/35",
                      )}
                      aria-hidden
                    >
                      <span
                        className={cn(
                          "rounded-full transition-all duration-200 ease-out",
                          item.isSelected
                            ? "h-2.5 w-2.5 scale-100 bg-primary-foreground opacity-100"
                            : "h-2 w-2 scale-50 bg-primary opacity-0",
                        )}
                      />
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};
